// The delivery holding-queue widget: how many arrivals are waiting to be matched to an order, and
// which ones (IM-004c, sprint 11).
//
// ── Why the contract is mirrored here rather than imported ────────────────────────────────────────
// `Sisolco.Dashboards` defines `DashboardWidget<TConfig, TData>` in TypeScript, but the published
// package ships only page entry modules and hashed chunks: there is no static-asset URL from which a
// consumer can import the type. The interface below is therefore a structural mirror — the widget
// satisfies the library's contract by shape, which is all the registry needs. Same reasoning, and the
// same library gap, as `checkReminderWidget.ts`.
//
// Plain DOM throughout: every value is written with `textContent`, never `innerHTML`. Delivery
// titles, project names and addresses are ordinary data a user can type, and a formatter that
// interpolated them into markup would be a stored-XSS sink.

import {
    buildQueueView,
    emptyText,
    projectLabel,
    receivedAtLabel,
    titleLabel,
    titleOf,
    waitingLabel,
    type DeliveryHoldingQueueConfig,
    type DeliveryHoldingQueueRow,
} from "./deliveryHoldingQueueModel";

/** The part of `WidgetRenderContext` this widget uses. Structural mirror; see the note above. */
export interface DeliveryHoldingQueueRenderContext {
    emitClick(intent: unknown, rowScope?: Record<string, unknown>): void;
    cellClickIntent?: unknown;
    localize?(text: string | null | undefined): string;
    editMode?: boolean;
    permissionDenied?: boolean;
}

/** The lifecycle the dashboard host drives. Structural mirror of the library's contract. */
export interface DeliveryHoldingQueueDashboardWidget {
    readonly widgetTypeKey: string;
    render(host: HTMLElement, config: DeliveryHoldingQueueConfig, data: unknown,
        ctx: DeliveryHoldingQueueRenderContext): void;
    update(config: DeliveryHoldingQueueConfig, data: unknown): void;
    dispose(): void;
}

/** Mirrors `KeyView.Procurement.KeyViewDeliveryWidgetKeys.HoldingQueue`. */
export const DeliveryHoldingQueueWidgetKey = "KeyView.DeliveryHoldingQueue";

export class DeliveryHoldingQueueWidget implements DeliveryHoldingQueueDashboardWidget {
    readonly widgetTypeKey: string;

    private host: HTMLElement | null = null;
    private root: HTMLElement | null = null;
    private titleEl: HTMLElement | null = null;
    private countEl: HTMLElement | null = null;
    private bodyEl: HTMLElement | null = null;
    private ctx: DeliveryHoldingQueueRenderContext | null = null;

    constructor(widgetTypeKey: string = DeliveryHoldingQueueWidgetKey) {
        this.widgetTypeKey = widgetTypeKey;
    }

    render(host: HTMLElement, config: DeliveryHoldingQueueConfig, data: unknown,
        ctx: DeliveryHoldingQueueRenderContext): void {
        this.host = host;
        this.ctx = ctx;
        host.innerHTML = "";

        const root = document.createElement("div");
        root.className = "keyview-delivery-holding-queue";

        const titleEl = document.createElement("div");
        titleEl.className = "title";

        const countEl = document.createElement("div");
        countEl.className = "queue-count";

        const bodyEl = document.createElement("div");
        bodyEl.className = "queue-body";

        root.appendChild(titleEl);
        root.appendChild(countEl);
        root.appendChild(bodyEl);
        host.appendChild(root);

        this.root = root;
        this.titleEl = titleEl;
        this.countEl = countEl;
        this.bodyEl = bodyEl;

        this.draw(config, data);
    }

    update(config: DeliveryHoldingQueueConfig, data: unknown): void {
        if (!this.root) return;
        this.draw(config, data);
    }

    dispose(): void {
        if (this.host) this.host.innerHTML = "";
        this.host = null;
        this.root = null;
        this.titleEl = null;
        this.countEl = null;
        this.bodyEl = null;
        this.ctx = null;
    }

    private text(value: string | null | undefined): string {
        const localize = this.ctx?.localize;
        return localize ? localize(value) : (value ?? "");
    }

    private draw(config: DeliveryHoldingQueueConfig, data: unknown): void {
        const body = this.bodyEl;
        const count = this.countEl;
        if (!body || !count) return;

        if (this.titleEl)
            this.titleEl.textContent = this.text(titleLabel(config));

        const view = buildQueueView(data);

        body.innerHTML = "";
        count.textContent = "";
        count.classList.toggle("is-clear", view.total === 0);

        if (view.total === 0) {
            count.textContent = "0";
            const none = document.createElement("div");
            none.className = "queue-empty";
            none.textContent = this.text(emptyText(config));
            body.appendChild(none);
            return;
        }

        // The figure IM-004c actually asks for. Drawn whether or not the list is shown, because a
        // narrow cell showing only this is a legitimate and useful configuration.
        count.textContent = String(view.total);

        if (config?.ShowList === false)
            return;

        const list = document.createElement("ul");
        list.className = "queue-list";

        for (const row of view.rows)
            list.appendChild(this.line(row, config));

        body.appendChild(list);

        // ⚠️ Said out loud rather than left to be inferred from a list that happens to stop. A reader
        // who counts the lines and gets a smaller number than the headline should be told why, not
        // left to wonder which of the two is wrong.
        if (view.truncated) {
            const more = document.createElement("div");
            more.className = "queue-more";
            more.textContent = this.text(
                `Showing the ${view.rows.length} longest waiting of ${view.total}.`);
            body.appendChild(more);
        }
    }

    private line(row: DeliveryHoldingQueueRow, config: DeliveryHoldingQueueConfig): HTMLElement {
        const li = document.createElement("li");
        li.className = "queue-row";

        const title = document.createElement("span");
        title.className = "delivery-title";
        title.textContent = this.text(titleOf(row));
        li.appendChild(title);

        const waiting = document.createElement("span");
        waiting.className = "delivery-waiting";
        waiting.textContent = this.text(waitingLabel(row));
        li.appendChild(waiting);

        if (config?.ShowProject !== false) {
            const project = document.createElement("span");
            project.className = "delivery-project";
            // ⚠️ Labelled rather than blank when there is no project — the ordinary state of an
            // arrival nobody can place yet, and a blank would read as missing data.
            project.textContent = this.text(projectLabel(row));
            li.appendChild(project);
        }

        if (config?.ShowReceivedAt !== false) {
            const where = receivedAtLabel(row);
            if (where) {
                const at = document.createElement("span");
                at.className = "delivery-received-at";
                at.textContent = this.text(where);
                li.appendChild(at);
            }
        }

        // Clicking a row asks the host to act on that delivery — the cell's own click intent, scoped
        // to this row. ⛔ The widget does not open a dialog itself: what a click means is the
        // dashboard author's to configure, and a widget that hard-wired one would override them.
        const ctx = this.ctx;
        if (ctx?.cellClickIntent && !ctx.editMode) {
            li.classList.add("is-clickable");
            li.addEventListener("click", () =>
                ctx.emitClick(ctx.cellClickIntent, { DeliveryId: row.DeliveryId }));
        }

        return li;
    }
}
